import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Doc } from '@vif/core';
import { READER_FONT_SIZES_PX } from '../app/settings.js';
import { useServices } from '../app/services.js';
import { QuickSettings } from '../components/QuickSettings.jsx';
import { WordDisplay } from '../components/WordDisplay.jsx';
import { createBrowserHost } from '../reader/browser-host.js';
import { anchorXForStage } from '../reader/orp-layout.js';
import { ReaderController, SPEED_PRESETS, WPM_STEP } from '../reader/controller.js';
import {
  addBookmark,
  appendSession,
  getBlocks,
  getBookmarks,
  getDocument,
  getPosition,
  removeBookmark,
  savePosition,
  setLangOverride,
  type BookmarkRow,
  type StoredDocument,
} from '../storage/index.js';

interface Loaded {
  readonly doc: StoredDocument;
  readonly controller: ReaderController;
}

type Panel = 'toc' | 'bookmarks' | null;

export function ReaderView(): ReactNode {
  const { db, t, settings, updateSettings } = useServices();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [bookmarks, setBookmarks] = useState<BookmarkRow[]>([]);
  const [panel, setPanel] = useState<Panel>(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const quickRestore = useRef<(() => void) | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [stageWidth, setStageWidth] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const readingRef = useRef<HTMLDivElement>(null);
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null);

  // ── load document + build controller ─────────────────────────────────────
  useEffect(() => {
    if (id === undefined) return;
    let disposed = false;
    let controller: ReaderController | undefined;

    void (async () => {
      const doc = await getDocument(db, id);
      if (doc === undefined) {
        void navigate('/library');
        return;
      }
      const blocks = await getBlocks(db, id);
      const position = await getPosition(db, id);
      setBookmarks(await getBookmarks(db, id));
      const readerDoc: Doc = { lang: doc.langOverride ?? doc.lang, blocks };
      controller = new ReaderController(
        {
          doc: readerDoc,
          wpm: settings.targetWpm,
          chunkSize: settings.chunkSize,
          adaptiveEnabled: settings.adaptiveEnabled,
          warmup: settings.warmupEnabled
            ? { startPct: settings.warmupStartPct, durationSec: settings.warmupDurationSec }
            : undefined,
          ...(position !== undefined && position.wordIdx > 0
            ? { startTokenIdx: position.wordIdx }
            : {}),
        },
        createBrowserHost(),
        {
          savePosition: (wordIdx) => void savePosition(db, id, wordIdx),
          appendSession: (row) =>
            void appendSession(db, {
              docId: id,
              date: new Date().toISOString().slice(0, 10),
              ...row,
            }),
        },
      );
      if (!disposed) setLoaded({ doc, controller });
      else controller.dispose();
    })();

    return () => {
      disposed = true;
      controller?.dispose();
      setLoaded(null);
    };
    // Controller wpm/chunk changes after mount go through controller
    // methods; rebuilding on every settings change would reset playback,
    // so settings are intentionally not dependencies here.
  }, [db, id, navigate]);

  // ── lifecycle wiring (PD-7 / PD-15) ──────────────────────────────────────
  useEffect(() => {
    if (loaded === null) return;
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') loaded.controller.handleHidden();
    };
    const onPageHide = (): void => loaded.controller.handlePageHide();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [loaded]);

  // ── stage sizing: anchor = 38% of the centered READING stage (H1). The
  // stage is width-capped in CSS; we simply measure the capped element.
  useEffect(() => {
    const stage = readingRef.current;
    if (stage === null || loaded === null) return;
    const observer = new ResizeObserver(() => setStageWidth(stage.clientWidth));
    observer.observe(stage);
    setStageWidth(stage.clientWidth);
    return () => observer.disconnect();
  }, [loaded]);

  const state = useSyncExternalStore(
    loaded?.controller.subscribe ?? (() => () => undefined),
    loaded?.controller.getState ?? (() => null),
  );

  // In-session speed changes persist as the new target (prototype behavior).
  const stateWpm = state?.wpm;
  useEffect(() => {
    if (stateWpm !== undefined && stateWpm !== settings.targetWpm) {
      updateSettings({ targetWpm: stateWpm });
    }
  }, [stateWpm]);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 1800);
  }, []);

  const toggleBookmark = useCallback(() => {
    if (loaded === null || state === null || id === undefined) return;
    const existing = bookmarks.find((b) => b.wordIdx === state.tokenIdx);
    void (async () => {
      if (existing !== undefined) await removeBookmark(db, id, existing.wordIdx);
      else {
        await addBookmark(db, id, state.tokenIdx, '');
        showToast(t('reader.bookmarkAdded'));
      }
      setBookmarks(await getBookmarks(db, id));
    })();
  }, [bookmarks, db, id, loaded, showToast, state, t]);

  // ── H2: quick settings suspend/resume playback around the popover ───────
  const openQuick = useCallback(() => {
    if (loaded === null) return;
    quickRestore.current = loaded.controller.suspendForUi();
    setQuickOpen(true);
  }, [loaded]);

  const closeQuick = useCallback(() => {
    setQuickOpen(false);
    quickRestore.current?.();
    quickRestore.current = null;
  }, []);

  // Live-apply settings the controller owns (H2): chunk size rebuilds the
  // chunk table in place; disabling warm-up cancels an active ramp (setWpm
  // to the current value is the engine's cancel path).
  useEffect(() => {
    loaded?.controller.setChunkSize(settings.chunkSize);
  }, [loaded, settings.chunkSize]);
  useEffect(() => {
    if (loaded !== null && !settings.warmupEnabled) {
      loaded.controller.setWpm(loaded.controller.getState().wpm);
    }
  }, [loaded, settings.warmupEnabled]);

  // ── keyboard map, exactly PRD §2.1 (+ S for quick settings, H2) ──────────
  useEffect(() => {
    if (loaded === null) return;
    const controller = loaded.controller;
    const onKey = (event: KeyboardEvent): void => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
        return;
      if (quickOpen) return; // the popover owns the keyboard while open (H2)
      switch (event.key) {
        case ' ':
          event.preventDefault();
          controller.toggle();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (event.ctrlKey) controller.paragraphBack();
          else controller.sentenceBack();
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (event.ctrlKey) controller.paragraphForward();
          else controller.sentenceForward();
          break;
        case 'ArrowUp':
          event.preventDefault();
          controller.adjustWpm(WPM_STEP);
          break;
        case 'ArrowDown':
          event.preventDefault();
          controller.adjustWpm(-WPM_STEP);
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          controller.setWpm(SPEED_PRESETS[Number(event.key) - 1]!);
          break;
        case 'r':
        case 'R':
          controller.restart();
          break;
        case 'f':
        case 'F':
          if (document.fullscreenElement === null)
            void document.documentElement.requestFullscreen();
          else void document.exitFullscreen();
          break;
        case 'b':
        case 'B':
          toggleBookmark();
          break;
        case 's':
        case 'S':
          openQuick();
          break;
        case 'Escape':
          if (panel !== null) setPanel(null);
          else void navigate('/library');
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loaded, navigate, panel, quickOpen, openQuick, toggleBookmark]);

  // ── touch: tap center = play/pause; swipe L/R = sentence ─────────────────
  const onPointerDown = useCallback((event: React.PointerEvent) => {
    swipeStart.current = { x: event.clientX, y: event.clientY, t: performance.now() };
  }, []);

  const onPointerUp = useCallback(
    (event: React.PointerEvent) => {
      const start = swipeStart.current;
      swipeStart.current = null;
      if (start === null || loaded === null) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 2) {
        if (dx < 0) loaded.controller.sentenceForward();
        else loaded.controller.sentenceBack();
        return;
      }
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
        const stage = stageRef.current;
        if (stage !== null) {
          const { left, width } = stage.getBoundingClientRect();
          const third = (event.clientX - left) / width;
          if (third > 0.25 && third < 0.75) loaded.controller.toggle();
        }
      }
    },
    [loaded],
  );

  const seekFromBar = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (loaded === null || state === null) return;
      const { left, width } = event.currentTarget.getBoundingClientRect();
      const fraction = Math.min(1, Math.max(0, (event.clientX - left) / width));
      loaded.controller.seekToken(Math.round(fraction * (state.totalTokens - 1)));
    },
    [loaded, state],
  );

  const currentTokens = useMemo(() => {
    if (loaded === null || state === null) return [];
    return loaded.controller.tokens.slice(state.tokenIdx, state.tokenIdx + state.tokenCount);
  }, [loaded, state]);

  if (loaded === null || state === null) return <div className="boot" />;

  const { controller, doc } = loaded;
  const progress = state.totalTokens === 0 ? 0 : state.tokenIdx / state.totalTokens;
  const minutesLeft = Math.ceil((state.totalTokens - state.tokenIdx) / Math.max(1, state.wpm));
  const anchorX = anchorXForStage(stageWidth);
  const chapterTitle = controller.chapters[state.chapterIdx]?.title ?? '';

  return (
    <div className="reader" style={{ ['--anchor-x' as string]: `${anchorX}px` }}>
      <div className="reader-top">
        <button className="btn btn-sm btn-ghost" onClick={() => void navigate('/library')}>
          ← {t('reader.back')}
        </button>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            textAlign: 'center',
          }}
        >
          {chapterTitle !== '' ? chapterTitle : doc.title}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className="icon-btn"
            aria-label={t('reader.contents')}
            onClick={() => setPanel(panel === 'toc' ? null : 'toc')}
          >
            ☰
          </button>
          <button
            className="icon-btn"
            aria-label={t('reader.bookmarks')}
            onClick={() => setPanel(panel === 'bookmarks' ? null : 'bookmarks')}
          >
            ⚑
          </button>
        </div>
      </div>

      <div
        className="reader-stage"
        ref={stageRef}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        <div
          className="reading-stage"
          ref={readingRef}
          role="img"
          aria-label={t('reader.rsvpLabel')}
        >
          {stageWidth > 0 && (
            <WordDisplay
              tokens={currentTokens}
              font={settings.font}
              fontSizePx={READER_FONT_SIZES_PX[settings.fontSize]}
              anchorX={anchorX}
              layout={settings.chunkLayout}
              showGuides={settings.showGuides}
              orpColor={settings.orpColor}
            />
          )}

          {state.overlay !== null && (
            <div className="chapter-overlay fade-up">
              <span className="kicker">{t('reader.chapter')}</span>
              <span className="title">{state.overlay.title}</span>
            </div>
          )}

          {state.finished && (
            <div className="chapter-overlay fade-up">
              <span className="title">{t('reader.finished')}</span>
              <button className="btn btn-sm" onClick={() => controller.restart()}>
                ↻
              </button>
            </div>
          )}
        </div>

        {quickOpen && <QuickSettings onClose={closeQuick} />}

        {panel !== null && (
          <aside className="side-panel fade-up">
            <h3>{panel === 'toc' ? t('reader.contents') : t('reader.bookmarks')}</h3>
            {panel === 'toc' &&
              controller.chapters.map((chapter, i) =>
                chapter.title === '' ? null : (
                  <button
                    key={i}
                    className={`entry ${i === state.chapterIdx ? 'current' : ''}`}
                    onClick={() => {
                      controller.seekToken(chapter.firstTokenIdx);
                      setPanel(null);
                    }}
                  >
                    {chapter.title}
                  </button>
                ),
              )}
            {panel === 'bookmarks' &&
              bookmarks.map((bookmark) => (
                <button
                  key={bookmark.wordIdx}
                  className="entry"
                  onClick={() => {
                    controller.seekToken(bookmark.wordIdx);
                    setPanel(null);
                  }}
                >
                  ⚑ {Math.round((bookmark.wordIdx / state.totalTokens) * 100)}% ·{' '}
                  {controller.tokens[bookmark.wordIdx]?.raw ?? ''}
                </button>
              ))}
            <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <h3>{t('settings.docLanguage')}</h3>
              <div className="seg">
                {(['auto', 'en', 'tr'] as const).map((value) => {
                  const active =
                    value === 'auto' ? doc.langOverride === undefined : doc.langOverride === value;
                  return (
                    <button
                      key={value}
                      className={active ? 'active' : ''}
                      onClick={() => {
                        void setLangOverride(db, doc.id, value === 'auto' ? undefined : value).then(
                          () => window.location.reload(),
                        );
                      }}
                    >
                      {value === 'auto'
                        ? t('settings.docLanguage.auto', { detected: doc.lang })
                        : value.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>
        )}
      </div>

      <div className="reader-bottom">
        <div className="stage-col">
          <div className="progress-bar" onClick={seekFromBar}>
            <div className="rail">
              <div className="fill" style={{ width: `${progress * 100}%` }} />
            </div>
            {bookmarks.map((bookmark) => (
              <span
                key={bookmark.wordIdx}
                className="bookmark-dot"
                style={{ left: `${(bookmark.wordIdx / state.totalTokens) * 100}%` }}
              />
            ))}
          </div>
          <div className="transport">
            <span style={{ minWidth: 70, fontSize: '0.8rem' }}>
              {Math.round(progress * 100)}% · {t('reader.timeLeft', { minutes: minutesLeft })}
            </span>
            <button
              className="icon-btn"
              onClick={() => controller.adjustWpm(-WPM_STEP)}
              aria-label="-25 WPM"
            >
              −
            </button>
            <button
              className="btn btn-primary"
              style={{ minWidth: 96 }}
              onClick={() => controller.toggle()}
            >
              {state.playing ? '❚❚' : '▶'}
            </button>
            <button
              className="icon-btn"
              onClick={() => controller.adjustWpm(WPM_STEP)}
              aria-label="+25 WPM"
            >
              +
            </button>
            <span className="wpm">
              {state.wpm} {t('reader.wpm')}
            </span>
          </div>
        </div>
      </div>

      {toast !== null && <div className="toast">{toast}</div>}
    </div>
  );
}
